import { useCallback, useMemo } from 'react';
import { doc, runTransaction } from 'firebase/firestore';
import { firestore } from '../../context/FirebaseConfig';
import { PRODUCTION_EVENT_DOCUMENT_PATH } from '../../shared_components/utils/FirebaseUtils';
import { formatDateTime, toDate } from '../../utils/timeUtils';
import {
  getCurrentRunningProduction,
  getNewestPendingMaterialCorrection,
  getMaterialCorrectionsForDevice,
} from '../../utils/recipes/recipeControl';

/**
 * Shared correction workflow helpers used across FeedTimestamping + RecipeDisplay.
 *
 * This hook centralizes:
 * - operator name resolution
 * - currently running production detection
 * - pending material correction lookup (from productionEvents, cross-production)
 * - correction date formatting
 * - correction lookup by deterministic key
 * - atomic correction updates in Firestore (targeting event documents directly)
 *
 * @param {Object} [params]
 * @param {Object|null} [params.currentUser] - Auth user with company context.
 * @param {Object|null} [params.shiftInfo] - Shift state containing active operator id.
 * @param {Object|null} [params.settings] - Client settings including users map.
 * @param {Array<Object>} [params.productionRuns=[]] - Live production runs.
 * @param {Array<Object>} [params.productionEvents=[]] - Live production event documents.
 * @param {Date} [params.now=new Date()] - Current timestamp used for "running run" resolution.
 * @param {string} [params.fallbackUserLabel='User'] - Fallback label when no operator can be resolved.
 */
export function useCorrection({
  currentUser,
  shiftInfo,
  settings,
  productionRuns = [],
  productionEvents = [],
  now = new Date(),
  fallbackUserLabel = 'User',
} = {}) {
  const activeUserName = useMemo(() => {
    if (shiftInfo?.user && settings?.users && settings.users[shiftInfo.user]) {
      const user = settings.users[shiftInfo.user];
      return `${user.firstname} ${user.lastname}`;
    }
    return currentUser?.displayName || currentUser?.email || fallbackUserLabel;
  }, [shiftInfo?.user, settings?.users, currentUser?.displayName, currentUser?.email, fallbackUserLabel]);

  const currentRunningProduction = useMemo(
    () => getCurrentRunningProduction(productionRuns, now),
    [productionRuns, now],
  );

  // Newest pending material correction for this device in the current production.
  const newestActionablePendingMaterialCorrection = useMemo(() => {
    if (!currentUser?.device_id) return null;

    const deviceMaterialCorrections = getMaterialCorrectionsForDevice(productionEvents, currentUser.device_id);
    const newest = getNewestPendingMaterialCorrection(deviceMaterialCorrections, { requireMaterials: true });
    if (!newest) return null;

    return { productionRun: currentRunningProduction, correction: newest };
  }, [productionEvents, currentUser?.device_id, currentRunningProduction]);

  const formatCorrectionDate = useCallback(
    (value) => formatDateTime(toDate(value), currentUser?.info?.languageCode),
    [currentUser?.info?.languageCode],
  );

  /**
   * Atomically marks a correction event document as confirmed by an operator.
   * Targets the event document directly by its Firestore ID.
   */
  const markCorrectionConfirmed = useCallback(
    async (eventDocId, correction, userName = activeUserName) => {
      if (!eventDocId || !correction || !userName) return;
      if (!currentUser?.company?.company) return;

      const confirmedBy = Array.isArray(correction?.confirmed_by) ? correction.confirmed_by : [];
      if (confirmedBy.includes(userName)) return;

      const eventDocRef = doc(firestore, PRODUCTION_EVENT_DOCUMENT_PATH(currentUser.company.company, eventDocId));

      try {
        await runTransaction(firestore, async (transaction) => {
          const eventSnap = await transaction.get(eventDocRef);
          if (!eventSnap.exists()) return;

          const existingConfirmedBy = Array.isArray(eventSnap.data()?.confirmed_by)
            ? eventSnap.data().confirmed_by
            : [];

          transaction.update(eventDocRef, {
            confirmed: true,
            confirmed_timestamp: new Date(),
            confirmed_by: Array.from(new Set([...existingConfirmedBy, userName])),
          });
        });
      } catch (error) {
        console.error('Failed to confirm correction:', error);
        throw error;
      }
    },
    [activeUserName, currentUser?.company?.company],
  );

  return {
    activeUserName,
    currentRunningProduction,
    newestActionablePendingMaterialCorrection,
    formatCorrectionDate,
    markCorrectionConfirmed,
  };
}
